import type { ReactNode } from 'react';
import { Text } from '@repletoryreach/sdk-ui';

/**
 * The furniture shared by the list pages.
 *
 * Flows, templates and contacts are the same screen with different columns.
 * These are the parts that were being written three times: a banner over the
 * table, a labelled filter, a status pill with a dot, the icon tile in front
 * of a row's name, and the detail-page header.
 */

/** A short standing note above a list: what this page does, or what went wrong. */
export function Banner({
  tone = 'info',
  children,
  action,
  onDismiss,
  dismissLabel
}: {
  tone?: 'info' | 'warning' | 'error';
  children: ReactNode;
  action?: ReactNode;
  onDismiss?: () => void;
  dismissLabel?: string;
}) {
  return (
    <div className={`rr-banner rr-banner--${tone}`} role={tone === 'error' ? 'alert' : undefined}>
      <span className="rr-banner__icon" aria-hidden>
        {tone === 'info' ? <InfoIcon /> : <WarnIcon />}
      </span>
      <span className="rr-banner__text">{children}</span>
      {action}
      {onDismiss && (
        <button type="button" className="rr-panel__clear" aria-label={dismissLabel} onClick={onDismiss}>
          ✕
        </button>
      )}
    </div>
  );
}

/** A filter with its name above it, rather than a placeholder that announces nothing. */
export function Filter({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="rr-panel__filter">
      <span className="rr-panel__filter-label">{label}</span>
      {children}
    </label>
  );
}

/** The search box, with the magnifier the reference puts inside it. */
export function SearchField({ children }: { children: ReactNode }) {
  return (
    <span className="rr-panel__search">
      <span className="rr-panel__search-icon" aria-hidden><SearchIcon /></span>
      {children}
    </span>
  );
}

export type PillTone = 'active' | 'paused' | 'approved' | 'submitted' | 'rejected' | 'draft' | 'neutral' | 'dev';

/**
 * A status, as a word with a dot.
 *
 * Never colour alone: the dot is the glance, the word is the answer, and both
 * survive a greyscale screen.
 */
export function StatusPill({ tone, children }: { tone: PillTone; children: ReactNode }) {
  return (
    <span className={`rr-statuspill rr-statuspill--${tone}`}>
      <span className="rr-statuspill__dot" aria-hidden />
      {children}
    </span>
  );
}

/** Just the dot, for a legend under a table. */
export function StatusDot({ tone, label }: { tone: PillTone; label: string }) {
  return (
    <span className="rr-panel__legend-item">
      <span className={`rr-statuspill__dot rr-statuspill--${tone}`} aria-hidden />
      {label}
    </span>
  );
}

/** A row's identity: a tinted icon tile, a name, and a line under it. */
export function RowName({
  icon, title, sub, tint
}: { icon: ReactNode; title: ReactNode; sub?: ReactNode; tint?: string }) {
  return (
    <span className="rr-rowname" style={tint ? ({ ['--rr-row-tint' as string]: tint }) : undefined}>
      <span className="rr-rowicon" aria-hidden>{icon}</span>
      <span className="rr-rowname__text">
        <span className="rr-rowname__title">{title}</span>
        {sub && <span className="rr-rowname__sub">{sub}</span>}
      </span>
    </span>
  );
}

/** The header of a detail page: who this is, and what can be done to it. */
export function DetailHeader({
  icon, tint, title, mono, pill, description, meta, actions, back
}: {
  icon: ReactNode;
  tint?: string;
  title: ReactNode;
  /** Render the title in the monospace face, for a code rather than a name. */
  mono?: boolean;
  pill?: ReactNode;
  description?: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  back?: ReactNode;
}) {
  return (
    <div style={tint ? ({ ['--rr-row-tint' as string]: tint }) : undefined}>
      <div className="rr-detailhead">
        <span className="rr-detailhead__icon" aria-hidden>{icon}</span>
        <div className="rr-detailhead__main">
          <div className="rr-detailhead__title">
            <h1 className={mono ? 'is-mono' : undefined}>{title}</h1>
            {pill}
          </div>
          {description && <Text role="bodySmall" c="var(--rr-color-text-muted)">{description}</Text>}
          {meta && <div className="rr-detailhead__meta">{meta}</div>}
        </div>
        {actions && <div className="rr-detailhead__actions">{actions}</div>}
      </div>
      {back}
    </div>
  );
}

export function SearchIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" aria-hidden>
      <circle cx="7" cy="7" r="4.5" />
      <path d="M10.4 10.4 L14 14" />
    </svg>
  );
}
export function FilterIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2 3.5h12L9.5 8.8V13L6.5 11.5V8.8z" />
    </svg>
  );
}
export function InfoIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" aria-hidden>
      <circle cx="8" cy="8" r="6.4" />
      <path d="M8 7.2v3.8" />
      <circle cx="8" cy="4.9" r="0.8" fill="currentColor" stroke="none" />
    </svg>
  );
}
export function WarnIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M8 1.9 15 14H1z" />
      <path d="M8 6.4v3.2" />
      <circle cx="8" cy="11.7" r="0.75" fill="currentColor" stroke="none" />
    </svg>
  );
}
export function PlusIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.9" strokeLinecap="round" aria-hidden>
      <path d="M8 3.2v9.6M3.2 8h9.6" />
    </svg>
  );
}
export function ExternalIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M6.5 3H3v10h10V9.5" />
      <path d="M9.5 2.5H13.5V6.5M13.5 2.5 8 8" />
    </svg>
  );
}
export function BackIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M13 8H3M6.5 4.5 3 8l3.5 3.5" />
    </svg>
  );
}
